// v2 bear — a hulking grizzly build: the massive shoulder hump as the tallest
// point, a big rounded rump, a lighter deep belly, stout columnar legs ending
// in broad flat paws, and a wide head carried low with a pale tapering muzzle
// and small round ears. Two coats: brown and dark cinnamon. Ported 1:1 from
// age-of-mcp's v2/bear.ts, using the library's shared `critter` (quadLegs)
// helper.
import * as THREE from "three";
import { defineModel, critter, type Part } from "../../lib";

const COATS = [
  { body: "#5a4026", hump: "#523a22", rump: "#563d24", belly: "#6b4e30", head: "#684a2e", leg: "#46311d", muzzle: "#96714a" },
  { body: "#47321f", hump: "#3f2c1b", rump: "#43301d", belly: "#57402a", head: "#533c26", leg: "#332414", muzzle: "#84613e" },
];

function bearParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.065, dz: 0.042, bodyY: 0.105, rTop: 0.016, rFoot: 0.0145, color: c.leg,
  });
  // broad flat paws, toes forward
  for (const sz of [1, -1]) {
    for (const sx of [1, -1]) {
      parts.push({
        geometry: new THREE.BoxGeometry(0.032, 0.014, 0.028), color: c.leg,
        position: [0.065 * sx + 0.01, 0.007, 0.042 * sz],
      });
    }
  }
  parts.push(
    // massive torso, the shoulder hump towering over it, big rump behind
    { geometry: new THREE.IcosahedronGeometry(0.068, 0), color: c.body,
      position: [-0.01, 0.105, 0], scale: [1.55, 1.05, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: c.hump,
      position: [0.038, 0.148, 0], scale: [1.0, 0.92, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.052, 0), color: c.rump,
      position: [-0.072, 0.112, 0], scale: [1.0, 0.95, 0.95] },
    // lighter deep belly
    { geometry: new THREE.IcosahedronGeometry(0.045, 0), color: c.belly,
      position: [0, 0.085, 0], scale: [1.3, 0.8, 0.95] },
    // broad head carried low
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.head,
      position: [0.102, 0.128, 0], scale: [1.15, 0.95, 0.95] },
    // pale tapering muzzle with a dark nose
    { geometry: new THREE.CylinderGeometry(0.012, 0.0155, 0.026, 5), color: c.muzzle,
      position: [0.138, 0.12, 0], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.0055, 0), color: "#181210",
      position: [0.152, 0.122, 0] },
    // small round ears
    { geometry: new THREE.IcosahedronGeometry(0.011, 0), color: c.leg,
      position: [0.089, 0.156, 0.0235] },
    { geometry: new THREE.IcosahedronGeometry(0.011, 0), color: c.leg,
      position: [0.089, 0.156, -0.0235] },
  );
  return parts;
}

export const bearV2 = defineModel({
  id: "bear-v2",
  scale: 2.09, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Bear (v2)",
  tags: ["creature","bear","low-poly","age-of-mcp","fable"],
  summary: "Low-poly bear.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.18,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => bearParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => bearParts(1) },
  ],
  animations: [],
});
